import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { NgModule, ErrorHandler } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ModalModule } from 'ngx-bootstrap/modal';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { SortableModule } from 'ngx-bootstrap/sortable';
import { TypeaheadModule } from 'ngx-bootstrap/typeahead';
import { NgbNavModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbDatepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { NgbTimepickerModule } from '@ng-bootstrap/ng-bootstrap';
import { FileUploadModule } from 'ng2-file-upload';
import { ClipboardModule } from 'ngx-clipboard';
import { CookieService } from 'ngx-cookie-service';
import { CommonModule } from '@angular/common';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';

import { environment } from '../environments/environment';  

import { CurrencyPipe } from '@angular/common';

// Guards
import { IntranetGuard } from './guards/intranet.guard';

// Rollbar

import { rollbarFactory } from './services/common/rollbar.service';
import { RollbarService } from './services/common/rollbar.service';
import { RollbarErrorHandler } from './rollbar-error-handler';

// Group imports by component folder or usage

// Appointment Requests
import { AppointmentRequestIndexComponent } from './components/appointment_requests/appointment-request-index/appointment-request-index.component';
import { AppointmentRequestShowComponent } from './components/appointment_requests/appointment-request-show/appointment-request-show.component';
import { AppointmentRequestNewComponent } from './components/appointment_requests/appointment-request-new/appointment-request-new.component';
import { AppointmentRequestEditComponent } from './components/appointment_requests/appointment-request-edit/appointment-request-edit.component';
import { AppointmentRequestFormComponent } from './components/appointment_requests/appointment-request-form/appointment-request-form.component';
import { AppointmentRequestResultFormComponent } from './components/appointment_requests/appointment-request-result-form/appointment-request-result-form.component';

// LawPay Payments
import { LawpayPaymentQueueComponent } from './components/lawpay/lawpay-payment-queue/lawpay-payment-queue.component';
import { LawpayProcessedPaymentsComponent } from './components/lawpay/lawpay-processed-payments/lawpay-processed-payments.component';
import { OnlinePaymentsIndexComponent } from './components/online-payments/online-payments-index/online-payments-index.component';

// Matter Types
import { MatterTypeIndexComponent } from './components/matter_types/matter-type-index/matter-type-index.component';
import { MatterTypeNewComponent } from './components/matter_types/matter-type-new/matter-type-new.component';
import { MatterTypeEditComponent } from './components/matter_types/matter-type-edit/matter-type-edit.component';
import { MatterTypeFormComponent } from './components/matter_types/matter-type-form/matter-type-form.component';

// Settings, Setting Groups and Setting Types
import { SettingGroupIndexComponent } from './components/setting_groups/setting-group-index/setting-group-index.component';
import { SettingGroupFormComponent } from './components/setting_groups/setting-group-form/setting-group-form.component';
import { SettingTypeIndexComponent } from './components/setting_types/setting-type-index/setting-type-index.component';
import { SettingTypeFormComponent } from './components/setting_types/setting-type-form/setting-type-form.component';
import { SettingIndexComponent } from './components/settings/setting-index/setting-index.component';
import { SettingFormComponent } from './components/settings/setting-form/setting-form.component';

// Documents
import { DocumentIndexComponent } from './components/documents/document-index/document-index.component';
import { DocumentInboxComponent } from './components/documents/document-inbox/document-inbox.component';

// Common  Components
import { ContactSearchComponent } from './components/common/contact-search/contact-search.component';
import { MatterSearchComponent } from './components/common/matter-search/matter-search.component';
import { TinymceComponent } from './components/common/tinymce/tinymce.component';
import { ButtonBackComponent } from './components/common/button-back/button-back.component';
import { IntranetSearchComponent } from './components/common/intranet-search/intranet-search.component';
import { TimepickerComponent } from './components/common/timepicker/timepicker.component';
import { DatepickerComponent } from './components/common/datepicker/datepicker.component';
import { ModalSmallComponent } from './components/common/modal-small/modal-small.component';
import { ModalComponent } from './components/common/modal/modal.component';
import { ModalLargeComponent } from './components/common/modal-large/modal-large.component';
import { DocumentSearchComponent } from './components/common/document-search/document-search.component';
import { DocumentTypeSearchComponent } from './components/common/document-type-search/document-type-search.component';
import { DocumentTypeTreeSelectComponent } from './components/common/document-type-tree-select/document-type-tree-select.component';
import { TagSearchComponent } from './components/common/tag-search/tag-search.component';
import { UserSearchComponent } from './components/common/user-search/user-search.component';

// Directives
import { AutocompleteDirective } from './directives/autocomplete.directive';
import { DatepickerDirective } from './directives/datepicker.directive';
import { UniqueValidatorDirective } from './directives/unique-validator.directive';

// Services
import { AuthService } from './services/common/auth.service';
import { AlertService } from './services/common/alert.service';

import { HttpClientService } from './services/common/http-client.service';

import { AddressService } from './services/contacts/address.service';
import { MatterService } from './services/matters/matter.service';
import { PhoneNumberService } from './services/contacts/phone-number.service';

import { ActionCableService } from './services/common/action-cable.service';

import { DocumentChannelService } from './services/channels/document-channel.service';
import { NotificationChannelService } from './services/channels/notification-channel.service';

import { AkaService } from './services/contacts/aka.service';
import { AppointmentRequestService } from './services/appointment_requests/appointment-request.service';
import { AutoEmailRuleService } from './services/auto_rules/auto-email-rule.service';
import { AutoOutgoingRuleService } from './services/auto_rules/auto-outgoing-rule.service';
import { AutoRedirectRuleService } from './services/auto_rules/auto-redirect-rule.service';
import { AutoReminderRuleService } from './services/auto_rules/auto-reminder-rule.service';
import { AutoTodoRuleService } from './services/auto_rules/auto-todo-rule.service';
import { BillService } from './services/accounting/bill.service';
import { BillingService } from './services/accounting/billing.service';
import { BrowserNotificationService } from './services/common/browser-notification.service';
import { BreadcrumbService } from './services/common/breadcrumb.service';
import { ClientIntakeService } from './services/client_intakes/client-intake.service';
import { ConflictService } from './services/conflicts/conflict.service';
import { ContactSearchService } from './services/contacts/contact-search.service';
import { ContactService } from './services/contacts/contact.service';
import { DataStoreService } from './services/common/data-store.service';
import { DetailService } from './services/matters/detail.service';
import { DocumentService } from './services/documents/document.service';
import { DocumentInboxService } from './services/documents/document-inbox.service';
import { DocumentTypeService } from './services/documents/document-type.service';
import { DocTemplateService } from './services/documents/doc-template.service';
import { DocumentTemplateRoleNameService } from './services/documents/document-template-role-name.service';
import { EventService } from './services/events/event.service';
import { FactorService } from './services/accounting/factor.service';
import { FaviconService } from './services/common/favicon.service';
import { FaxService } from './services/outgoing/fax.service';
import { GlobalTaxService } from './services/accounting/global-tax.service';
import { IntranetSearchService } from './services/common/intranet-search.service';
import { InvoiceService } from './services/accounting/invoice.service';
import { InvolvementService } from './services/matters/involvement.service';
import { LedgerService } from './services/accounting/ledger.service';
import { LinkedAccountService } from './services/common/linked-account.service';
import { LoggerService } from './services/common/logger.service';
import { MatterDateService } from './services/matters/matter-date.service';
import { MatterHistoryService } from './services/matters/matter-history.service';
import { MatterSearchService } from './services/matters/matter-search.service';
import { MatterStoreService } from './services/matters/matter-store.service';
import { MatterTypeService } from './services/matters/matter-type.service';
import { MatterUserService } from './services/matters/matter-user.service';
import { ModalService } from './services/common/modal.service';
import { NewsletterMailinglistService } from './services/newsletters/newsletter-mailinglist.service';
import { NotyService } from './services/common/noty.service';
import { PaymentObligationService } from './services/accounting/payment-obligation.service';
import { OralDepositionService } from './services/oral_depositions/oral-deposition.service';
import { OutgoingCheckService } from './services/outgoing/outgoing-check.service';
import { OutgoingDocumentService } from './services/outgoing/outgoing-document.service';
import { OutgoingEnvelopeService } from './services/outgoing/outgoing-envelope.service';
import { OutgoingJobService } from './services/outgoing/outgoing-job.service';
import { OutgoingLabelService } from './services/outgoing/outgoing-label.service';
import { PhonecallService } from './services/users/phonecall.service';
import { QueryParamsService } from './services/common/query-params.service';
import { RelationshipService } from './services/contacts/relationship.service';
import { ReminderService } from './services/events/reminder.service';
import { RruleService } from './services/events/rrule.service';
import { SettingService } from './services/settings/setting.service';
import { SettingGroupService } from './services/settings/setting-group.service';
import { SettingTypeService } from './services/settings/setting-type.service';
import { SidekiqService } from './services/common/sidekiq.service';
import { SpamTitanService } from './services/contacts/spam-titan.service';
import { StylesheetService } from './services/documents/stylesheet.service';
import { TagService } from './services/common/tag.service';
import { TaxService } from './services/accounting/tax.service';
import { TaxObligationService } from './services/accounting/tax-obligation.service';
import { TextDocumentService } from './services/texts/text-document.service';
import { TextDocumentTemplateService } from './services/texts/text-document-template.service';
import { ToBePaidService } from './services/accounting/to-be-paid.service';
import { TodoService } from './services/events/todo.service';
import { UserService } from './services/users/user.service';
import { ValidationFunctionsService } from './services/common/validation-functions.service';
import { VendorService } from './services/accounting/vendor.service';
import { VendorSearchService } from './services/accounting/vendor-search.service';
import { WageBaseService } from './services/users/wage-base.service';
import { WithheldIncomeTaxService } from './services/accounting/withheld-income-tax.service';
import { SharesService } from './services/shares/shares.service';
import { EmailService } from './services/emails/email.service';
import { ActivityMatterRatesService } from './services/activity_matter_rates/activity-matter-rates.service';

// App routes
import { AppRoutingModule } from './app-routing.module';

// App root component
import { AppComponent } from './app.component';
import { AngularTestComponent } from './components/test/angular-test/angular-test.component';
import { MatterShowComponent } from './components/matters/matter-show/matter-show.component';
import { NavbarComponent } from './components/navbar/navbar/navbar.component';
import { MatterInfoComponent } from './components/matters/matter-info/matter-info.component';
import { SafeHtmlPipe } from './pipes/safe-html.pipe';
import { InvolvementsComponent } from './components/involvements/involvements/involvements.component';
import { InvolvementFormComponent } from './components/involvements/involvement-form/involvement-form.component';
import { InvolvementComponent } from './components/involvements/involvement/involvement.component';
import { MatterSidebarComponent } from './components/matters/matter-sidebar/matter-sidebar.component';
import { BillingsIndexComponent } from './components/billings/billings-index/billings-index.component';
import { BillIndexComponent } from './components/bills/bill-index/bill-index.component';
import { InvoiceShowComponent } from './components/invoices/invoice-show/invoice-show.component';
import { BillShowComponent } from './components/bills/bill-show/bill-show.component';
import { MatterHistoriesComponent } from './components/matters/matter-histories/matter-histories.component';
import { MatterHistoryFormComponent } from './components/matter_histories/matter-history-form/matter-history-form.component';
import { MatterAssociationsComponent } from './components/matters/matter-associations/matter-associations.component';
import { MatterExhibitsComponent } from './components/matters/matter-exhibits/matter-exhibits.component';
import { MatterEventsComponent } from './components/matters/matter-events/matter-events.component';
import { MatterBillingSummaryComponent } from './components/matters/matter-billing-summary/matter-billing-summary.component';
import { OutgoingJobsIndexComponent } from './components/outgoing/outgoing-jobs-index/outgoing-jobs-index.component';
import { MatterStatusComponent } from './components/matters/matter-status/matter-status.component';
import { MatterUserIndexComponent } from './components/matter_users/matter-user-index/matter-user-index.component';
import { MatterUserFormComponent } from './components/matter_users/matter-user-form/matter-user-form.component';
import { MatterDateIndexComponent } from './components/matter_dates/matter-date-index/matter-date-index.component';
import { MatterDateFormComponent } from './components/matter_dates/matter-date-form/matter-date-form.component';
import { MatterDetailFormComponent } from './components/matter_details/matter-detail-form/matter-detail-form.component';
import { MatterDetailIndexComponent } from './components/matter_details/matter-detail-index/matter-detail-index.component';
import { MatterDocumentsListComponent } from './components/matters/matter-documents-list/matter-documents-list.component';
import { DocumentFormUploadComponent } from './components/documents/document-form-upload/document-form-upload.component';
import { MatterEditFormComponent } from './components/matters/matter-edit-form/matter-edit-form.component';
import { MatterTagsComponent } from './components/matters/matter-tags/matter-tags.component';
import { TinymceCssComponent } from './components/common/tinymce-css/tinymce-css.component';
import { DocumentFormComponent } from './components/documents/document-form/document-form.component';
import { DocTemplateSearchComponent } from './components/common/doc-template-search/doc-template-search.component';
import { ModalConfirmComponent } from './components/common/modal-confirm/modal-confirm.component';
import { DocumentSidebarComponent } from './components/documents/document-sidebar/document-sidebar.component';
import { VendorIndexComponent } from './components/vendors/vendor-index/vendor-index.component';
import { VendorShowComponent } from './components/vendors/vendor-show/vendor-show.component';
import { VendorFormComponent } from './components/vendors/vendor-form/vendor-form.component';
import { VendorNewComponent } from './components/vendors/vendor-new/vendor-new.component';
import { VendorEditComponent } from './components/vendors/vendor-edit/vendor-edit.component';
import { ToBePaidsComponent } from './components/to_be_paids/to-be-paids/to-be-paids.component';
import { ToBePaidFormComponent } from './components/to_be_paids/to-be-paid-form/to-be-paid-form.component';
import { ToBePaidNewComponent } from './components/to_be_paids/to-be-paid-new/to-be-paid-new.component';
import { ToBePaidEditComponent } from './components/to_be_paids/to-be-paid-edit/to-be-paid-edit.component';
import { VendorSearchComponent } from './components/common/vendor-search/vendor-search.component';
import { OutgoingFormDialogComponent } from './components/outgoing/outgoing-form-dialog/outgoing-form-dialog.component';
import { OutgoingFormDialogAttachmentsComponent } from './components/outgoing/outgoing-form-dialog-attachments/outgoing-form-dialog-attachments.component';
import { ToBePaidComponent } from './components/to_be_paids/to-be-paid/to-be-paid.component';
import { ReminderIndexComponent } from './components/reminders/reminder-index/reminder-index.component';
import { ReminderFormComponent } from './components/reminders/reminder-form/reminder-form.component';
import { BillingRecordTimeComponent } from './components/billings/billing-record-time/billing-record-time.component';
import { DocumentShowComponent } from './components/documents/document-show/document-show.component';
import { SafeUrlPipe } from './pipes/safe-url.pipe';
import { ConfirmDialogComponent } from './components/common/confirm-dialog/confirm-dialog.component';
import { AlertDialogComponent } from './components/common/alert-dialog/alert-dialog.component';
import { MatterAdministrationComponent } from './components/matters/matter-administration/matter-administration.component';
import { UserShowComponent } from './components/users/user-show/user-show.component';
import { EventsIndexComponent } from './components/events/events-index/events-index.component';
import { EventShowComponent } from './components/events/event-show/event-show.component';
import { EventFormComponent } from './components/events/event-form/event-form.component';
import { EventFullcalendarComponent } from './components/events/event-fullcalendar/event-fullcalendar.component';
import { EventMultidayComponent } from './components/events/event-multiday/event-multiday.component';
import { TodosComponent } from './components/todos/todos/todos.component';
import { TodoFormComponent } from './components/todos/todo-form/todo-form.component';
import { EventHomeComponent } from './components/events/event-home/event-home.component';
import { EventComponent } from './components/events/event/event.component';
import { AuthorSearchComponent } from './components/common/author-search/author-search.component';
import { RecipientSearchComponent } from './components/common/recipient-search/recipient-search.component';
import { MatterTodoFormComponent } from './components/matters/matter-todo-form/matter-todo-form.component';
import { BreadcrumbsComponent } from './components/breadcrumbs/breadcrumbs/breadcrumbs.component';
import { UserIndexComponent } from './components/users/user-index/user-index.component';
import { NewsletterIndexComponent } from './components/newsletters/newsletter-index/newsletter-index.component';
import { IntranetIndexComponent } from './components/intranets/intranet-index/intranet-index.component';
import { ClientIntakeFieldIndexComponent } from './components/client-intake-fields/client-intake-field-index/client-intake-field-index.component';
import { TaxIndexComponent } from './components/taxes/tax-index/tax-index.component';
import { GlobalTaxIndexComponent } from './components/global-taxes/global-tax-index/global-tax-index.component';
import { GlobalTaxFormComponent } from './components/global-taxes/global-tax-form/global-tax-form.component';
import { TaxFormComponent } from './components/taxes/tax-form/tax-form.component';
import { TaxShowComponent } from './components/taxes/tax-show/tax-show.component';
import { NumberValidatorDirective } from './directives/number-validator.directive';
import { MinNumberValidatorDirective } from './directives/min-number-validator.directive';
import { MaxNumberValidatorDirective } from './directives/max-number-validator.directive';
import { WithheldIncomeTaxIndexComponent } from './components/withheld-income-taxes/withheld-income-tax-index/withheld-income-tax-index.component';
import { WithheldIncomeTaxFormComponent } from './components/withheld-income-taxes/withheld-income-tax-form/withheld-income-tax-form.component';
import { TaxObligationIndexComponent } from './components/tax-obligations/tax-obligation-index/tax-obligation-index.component';
import { OralDepositionIndexComponent } from './components/oral-depositions/oral-deposition-index/oral-deposition-index.component';
import { OralDepositionFormComponent } from './components/oral-depositions/oral-deposition-form/oral-deposition-form.component';
import { PaymentObligationIndexComponent } from './components/payment-obligations/payment-obligation-index/payment-obligation-index.component';
import { PageNotFoundComponent } from './components/errors/page-not-found/page-not-found.component';
import { OralDepositionShowComponent } from './components/oral-depositions/oral-deposition-show/oral-deposition-show.component';
import { DocumentTemplateRoleNameIndexComponent } from './components/document-template-role-names/document-template-role-name-index/document-template-role-name-index.component';
import { DocumentTypeIndexComponent } from './components/document_types/document-type-index/document-type-index.component';
import { DocumentTypeListComponent } from './components/document_types/document-type-list/document-type-list.component';
import { AutoEmailRuleIndexComponent } from './components/auto_email_rules/auto-email-rule-index/auto-email-rule-index.component';
import { AutoOutgoingRuleIndexComponent } from './components/auto_outgoing_rules/auto-outgoing-rule-index/auto-outgoing-rule-index.component';
import { AutoRedirectRuleIndexComponent } from './components/auto_redirect_rules/auto-redirect-rule-index/auto-redirect-rule-index.component';
import { AutoTodoRuleIndexComponent } from './components/auto_todo_rules/auto-todo-rule-index/auto-todo-rule-index.component';
import { AutoEmailRuleFormComponent } from './components/auto_email_rules/auto-email-rule-form/auto-email-rule-form.component';
import { AutoOutgoingRuleFormComponent } from './components/auto_outgoing_rules/auto-outgoing-rule-form/auto-outgoing-rule-form.component';
import { AutoRedirectRuleFormComponent } from './components/auto_redirect_rules/auto-redirect-rule-form/auto-redirect-rule-form.component';
import { AutoTodoRuleFormComponent } from './components/auto_todo_rules/auto-todo-rule-form/auto-todo-rule-form.component';
import { FullCalendarComponent } from './components/common/full-calendar/full-calendar.component';
import { UserEditComponent } from './components/users/user-edit/user-edit.component';
import { UserNewComponent } from './components/users/user-new/user-new.component';
import { UserFormComponent } from './components/users/user-form/user-form.component';
import { WageBaseIndexComponent } from './components/wage_bases/wage-base-index/wage-base-index.component';
import { WageBaseFormComponent } from './components/wage_bases/wage-base-form/wage-base-form.component';
import { WageBaseShowComponent } from './components/wage_bases/wage-base-show/wage-base-show.component';
import { LedgerAdjustmentFormComponent } from './components/accounting/ledger_adjustments/ledger-adjustment-form/ledger-adjustment-form.component';
import { LedgerNewComponent } from './components/accounting/ledgers/ledger-new/ledger-new.component';
import { SidekiqIndexComponent } from './components/sidekiq/sidekiq-index/sidekiq-index.component';
import { ContactIndexComponent } from './components/contacts/contact-index/contact-index.component';
import { ContactShowComponent } from './components/contacts/contact-show/contact-show.component';
import { ContactSidebarComponent } from './components/contacts/contact-sidebar/contact-sidebar.component';
import { ClipboardBtnComponent } from './components/common/clipboard-btn/clipboard-btn.component';
import { AddressIndexComponent } from './components/addresses/address-index/address-index.component';
import { AddressFormComponent } from './components/addresses/address-form/address-form.component';
import { PhoneNumberIndexComponent } from './components/phone-numbers/phone-number-index/phone-number-index.component';
import { PhoneNumberFormComponent } from './components/phone-numbers/phone-number-form/phone-number-form.component';
import { AkaIndexComponent } from './components/akas/aka-index/aka-index.component';
import { AkaFormComponent } from './components/akas/aka-form/aka-form.component';
import { ContactTagsComponent } from './components/contacts/contact-tags/contact-tags.component';
import { ContactRelationshipsComponent } from './components/contacts/contact-relationships/contact-relationships.component';
import { ContactFormComponent } from './components/contacts/contact-form/contact-form.component';
import { ContactNewsletterMailinglistsComponent } from './components/contacts/contact-newsletter-mailinglists/contact-newsletter-mailinglists.component';
import { EmailAddressIndexComponent } from './components/email_addresses/email-address-index/email-address-index.component';
import { EmailAddressFormComponent } from './components/email_addresses/email-address-form/email-address-form.component';
import { ContactVendorsComponent } from './components/contacts/contact-vendors/contact-vendors.component';
import { ContactVendorFormComponent } from './components/contacts/contact-vendor-form/contact-vendor-form.component';
import { ContactNewComponent } from './components/contacts/contact-new/contact-new.component';
import { ReminderCommonFormComponent } from './components/reminders/reminder-common-form/reminder-common-form.component';
import { ReminderCommonComponent } from './components/reminders/reminder-common/reminder-common.component';
import { DatetimePickerComponent } from './components/common/datetime-picker/datetime-picker.component';
import { AutoReminderRuleFormComponent } from './components/auto_reminder_rules/auto-reminder-rule-form/auto-reminder-rule-form.component';
import { AutoReminderRuleIndexComponent } from './components/auto_reminder_rules/auto-reminder-rule-index/auto-reminder-rule-index.component';
import { PhonecallsIndexComponent } from './components/phonecalls/phonecalls-index/phonecalls-index.component';
import { OutgoingJobsShowComponent } from './components/outgoing/outgoing-jobs-show/outgoing-jobs-show.component';
import { FaxIndexComponent } from './components/faxes/fax-index/fax-index.component';
import { ScanPriorityIndexComponent } from './components/scan_priorities/scan-priority-index/scan-priority-index.component';
import { DocTemplateIndexComponent } from './components/doc_templates/doc-template-index/doc-template-index.component';
import { DocTemplateFormComponent } from './components/doc_templates/doc-template-form/doc-template-form.component';
import { StylesheetSearchComponent } from './components/common/stylesheet-search/stylesheet-search.component';
import { HunspellSpellCheckIndexComponent } from './components/hunspell_spell_check/hunspell-spell-check-index/hunspell-spell-check-index.component';
import { MatterUserIncomeComponent } from './components/matter_users/matter-user-income/matter-user-income.component';
import { MatterIndexComponent } from './components/matters/matter-index/matter-index.component';
import { IntranetShowComponent } from './components/intranets/intranet-show/intranet-show.component';
import { IntranetFormComponent } from './components/intranets/intranet-form/intranet-form.component';
import { ClientIntakeFieldFormComponent } from './components/client-intake-fields/client-intake-field-form/client-intake-field-form.component';
import { ClientIntakeFieldTypeIndexComponent } from './components/client-intake-field-types/client-intake-field-type-index/client-intake-field-type-index.component';
import { ClientIntakeFieldTypeFormComponent } from './components/client-intake-field-types/client-intake-field-type-form/client-intake-field-type-form.component';
import { ClientIntakeIndexComponent } from './components/client-intakes/client-intake-index/client-intake-index.component';
import { ClientIntakeFormComponent } from './components/client-intakes/client-intake-form/client-intake-form.component';
import { ClientIntakeShowComponent } from './components/client-intakes/client-intake-show/client-intake-show.component';
import { ConflictCheckComponent } from './components/conflicts/conflict-check/conflict-check.component';
import { NewsletterFormComponent } from './components/newsletters/newsletter-form/newsletter-form.component';
import { NewsletterShowComponent } from './components/newsletters/newsletter-show/newsletter-show.component';
import { NewsletterMailcampaignFormComponent } from './components/newsletters/newsletter-mailcampaign-form/newsletter-mailcampaign-form.component';
import { NewsletterFooterIndexComponent } from './components/newsletters/newsletter-footer-index/newsletter-footer-index.component';
import { NewsletterFooterFormComponent } from './components/newsletters/newsletter-footer-form/newsletter-footer-form.component';
import { NewsletterMailinglistIndexComponent } from './components/newsletters/newsletter-mailinglist-index/newsletter-mailinglist-index.component';
import { DocPartIndexComponent } from './components/doc_parts/doc-part-index/doc-part-index.component';
import { TextDocumentIndexComponent } from './components/texts/text-document-index/text-document-index.component';
import { TextTemplateSearchComponent } from './components/common/text-template-search/text-template-search.component';
import { SharesNewComponent } from './components/shares/shares-new/shares-new.component';
import { EmailNewComponent } from './components/emails/email-new/email-new.component';
import { StylesheetGroupsComponent } from './components/stylesheet/stylesheet-groups/stylesheet-groups.component';
import { StylesheetsIndexComponent } from './components/stylesheets/stylesheets-index/stylesheets-index.component';
import { StylesheetsFormComponent } from './components/stylesheets/stylesheets-form/stylesheets-form.component';
import { FontIndexComponent } from './components/fonts/font-index/font-index.component';
import { FontShowComponent } from './components/fonts/font-show/font-show.component';
import { NewsletterFooterShowComponent } from './components/newsletters/newsletter-footer-show/newsletter-footer-show.component';
import { EmailEmbeddedImageSupportIndexComponent } from './components/email-embedded-image-support/email-embedded-image-support-index/email-embedded-image-support-index.component';
import { ScanIndexComponent } from './components/scans/scan-index/scan-index.component';
import { ScanEditComponent } from './components/scans/scan-edit/scan-edit.component';
import { BillingIndexComponent } from './components/accounting/billings/billing-index/billing-index.component';
import { BillsIndexComponent } from './components/accounting/bills/bills-index/bills-index.component';
import { AccountingDashboardComponent } from './components/accounting/accounting-dashboard/accounting-dashboard.component';
import { FactorIndexComponent } from './components/accounting/factors/factor-index/factor-index.component';
import { LedgerIndexComponent } from './components/accounting/ledgers/ledger-index/ledger-index.component';
import { CheckIndexComponent } from './components/accounting/checks/check-index/check-index.component';
import { DepositIndexComponent } from './components/accounting/deposits/deposit-index/deposit-index.component';
import { ReceiptIndexComponent } from './components/accounting/receipts/receipt-index/receipt-index.component';
import { ReceiptNewComponent } from './components/accounting/receipts/receipt-new/receipt-new.component';
import { EntryGroupIndexComponent } from './components/accounting/entry-groups/entry-group-index/entry-group-index.component';
import { ReceivableIndexComponent } from './components/accounting/receivables/receivable-index/receivable-index.component';
import { WageIndexComponent } from './components/accounting/wages/wage-index/wage-index.component';
import { MultiSelectSearchComponent } from './components/common/multi-select-search/multi-select-search.component';
import { FormTextInputComponent } from './components/common/form-text-input/form-text-input.component';
import { FormCheckboxComponent } from './components/common/form-checkbox/form-checkbox.component';
import { InvoiceIndexComponent } from './components/accounting/invoices/invoice-index/invoice-index.component';
import { FormHelpTextComponent } from './components/common/form-help-text/form-help-text.component';
import { FormSelectComponent } from './components/common/form-select/form-select.component';
import { PaginationComponent } from './components/common/pagination/pagination.component';
import { FormTextAreaComponent } from './components/common/form-text-area/form-text-area.component';
import { FormDatePickerComponent } from './components/common/form-date-picker/form-date-picker.component';
import { PageLoaderComponent } from './components/common/page-loader/page-loader.component';
import { MatterInvoiceIndexComponent } from './components/invoices/matter-invoice-index/matter-invoice-index.component';
import { BadgeComponent } from './components/common/badge/badge.component';
import { PaginationCountComponent } from './components/common/pagination-count/pagination-count.component';
import { OutgoingJobStatusComponent } from './components/outgoing/outgoing-job-status/outgoing-job-status.component';
import { OutgoingItemStatusComponent } from './components/outgoing/outgoing-item-status/outgoing-item-status.component';
import { FaxStatusComponent } from './components/faxes/fax-status/fax-status.component';
import { OralDepositionStatusComponent } from './components/oral-depositions/oral-deposition-status/oral-deposition-status.component';
import { ButtonComponent } from './components/common/button/button.component';
import { SearchBtnComponent } from './components/common/search-btn/search-btn.component';
import { ClearSearchBtnComponent } from './components/common/clear-search-btn/clear-search-btn.component';
import { SidebarToggleBtnComponent } from './components/common/sidebar-toggle-btn/sidebar-toggle-btn.component';
import { FormGroupComponent } from './components/common/form-group/form-group.component';
import { IconComponent } from './components/common/icon/icon.component';
import { AppointmentRequestsSidebarComponent } from './components/navbar/appointment-requests-sidebar/appointment-requests-sidebar.component';
import { NativeSortableJsDirective } from './directives/native-sortable-js.directive';
import { PhoneCallBillingDirective } from './directives/phone-call-billing.directive';
import { PhoneCallBillingModalComponent } from './components/phone-call-billing-modal/phone-call-billing-modal.component';
import { PhoneCallBillingService } from './services/phone-calls/phone-call-billing.service';
import { FormLabelComponent } from './components/common/form-label/form-label.component';
import { SpinnerComponent } from './components/common/spinner/spinner.component';
import { LoadingTextComponent } from './components/common/loading-text/loading-text.component';
import { LinkedAccountsComponent } from './components/intranets/linked-accounts/linked-accounts.component';
import { SimpleModelFormComponent } from './components/common/simple-model-form/simple-model-form.component';
import { GeoSearchComponent } from './components/common/geo-search/geo-search.component';
import { FormControlComponent } from './components/common/form-control/form-control.component';
import { FormFieldComponent } from './components/abstract/form-field/form-field.component';
import { ActivityMatterRatesIndexComponent } from './components/activity_matter_rates/activity-matter-rates-index/activity-matter-rates-index.component';
import { ActivityMatterRatesShowComponent } from './components/activity_matter_rates/activity-matter-rates-show/activity-matter-rates-show.component';
import { DashboardReminderComponent } from './components/reminders/dashboard-reminder/dashboard-reminder.component';
import { ManualIndexComponent } from './components/manual/manual-index/manual-index.component';
import { ManualSearchPipe } from './components/manual/manual-search.pipe';

if(environment.test) {
  // Local backend for testing
  import('./tests/mirage/server').then(({ createMirageServer }) => createMirageServer());
}

@NgModule({
  declarations: [
    AppointmentRequestIndexComponent,
    AppointmentRequestShowComponent,
    AppointmentRequestNewComponent,
    AppointmentRequestFormComponent,
    AppointmentRequestResultFormComponent,
    AppointmentRequestEditComponent,
    LawpayPaymentQueueComponent,
    LawpayProcessedPaymentsComponent,
    OnlinePaymentsIndexComponent,

    MatterTypeIndexComponent,
    MatterTypeNewComponent,
    MatterTypeEditComponent,
    MatterTypeFormComponent,

    SettingGroupIndexComponent,
    SettingGroupFormComponent,
    SettingTypeIndexComponent,
    SettingTypeFormComponent,
    SettingIndexComponent,
    SettingFormComponent,

    ContactSearchComponent,
    MatterSearchComponent,
    TinymceComponent,
    ButtonBackComponent,
    IntranetSearchComponent,
    TimepickerComponent,
    DatepickerComponent,
    ModalSmallComponent,
    ModalComponent,
    ModalLargeComponent,
    DocumentSearchComponent,
    DocumentTypeSearchComponent,
    DocumentTypeTreeSelectComponent,
    TagSearchComponent,
    UserSearchComponent,

    AppComponent,

    AutocompleteDirective,
    DatepickerDirective,
    UniqueValidatorDirective,

    DocumentIndexComponent,
    DocumentInboxComponent,
    AngularTestComponent,
    MatterShowComponent,
    NavbarComponent,
    MatterInfoComponent,
    SafeHtmlPipe,
    InvolvementsComponent,
    InvolvementFormComponent,
    InvolvementComponent,
    MatterSidebarComponent,
    BillingsIndexComponent,
    BillIndexComponent,
    InvoiceShowComponent,
    BillShowComponent,
    MatterHistoriesComponent,
    MatterHistoryFormComponent,
    MatterAssociationsComponent,
    MatterExhibitsComponent,
    MatterEventsComponent,
    MatterBillingSummaryComponent,
    InvoiceIndexComponent,
    OutgoingJobsIndexComponent,
    MatterStatusComponent,
    MatterUserIndexComponent,
    MatterUserFormComponent,
    MatterDateIndexComponent,
    MatterDateFormComponent,
    MatterDetailFormComponent,
    MatterDetailIndexComponent,
    MatterDocumentsListComponent,
    DocumentFormUploadComponent,
    MatterEditFormComponent,
    MatterTagsComponent,
    TinymceCssComponent,
    DocumentFormComponent,
    DocTemplateSearchComponent,
    ModalConfirmComponent,
    DocumentSidebarComponent,
    VendorIndexComponent,
    VendorShowComponent,
    VendorFormComponent,
    VendorNewComponent,
    VendorEditComponent,
    ToBePaidsComponent,
    ToBePaidFormComponent,
    ToBePaidNewComponent,
    ToBePaidEditComponent,
    VendorSearchComponent,
    OutgoingFormDialogComponent,
    OutgoingFormDialogAttachmentsComponent,
    ToBePaidComponent,
    ReminderIndexComponent,
    ReminderFormComponent,
    BillingRecordTimeComponent,
    DocumentShowComponent,
    SafeUrlPipe,
    ConfirmDialogComponent,
    AlertDialogComponent,
    MatterAdministrationComponent,
    UserShowComponent,
    EventsIndexComponent,
    EventShowComponent,
    EventFormComponent,
    EventFullcalendarComponent,
    EventMultidayComponent,
    TodosComponent,
    TodoFormComponent,
    EventHomeComponent,
    EventComponent,
    AuthorSearchComponent,
    RecipientSearchComponent,
    MatterTodoFormComponent,
    BreadcrumbsComponent,
    UserIndexComponent,
    NewsletterIndexComponent,
    IntranetIndexComponent,
    ClientIntakeFieldIndexComponent,
    TaxIndexComponent,
    GlobalTaxIndexComponent,
    GlobalTaxFormComponent,
    TaxFormComponent,
    TaxShowComponent,
    NumberValidatorDirective,
    MinNumberValidatorDirective,
    MaxNumberValidatorDirective,
    WithheldIncomeTaxIndexComponent,
    WithheldIncomeTaxFormComponent,
    TaxObligationIndexComponent,
    OralDepositionIndexComponent,
    OralDepositionFormComponent,
    PaymentObligationIndexComponent,
    PageNotFoundComponent,
    OralDepositionShowComponent,
    DocumentTemplateRoleNameIndexComponent,
    DocumentTypeIndexComponent,
    DocumentTypeListComponent,
    AutoEmailRuleIndexComponent,
    AutoOutgoingRuleIndexComponent,
    AutoRedirectRuleIndexComponent,
    AutoTodoRuleIndexComponent,
    AutoEmailRuleFormComponent,
    AutoOutgoingRuleFormComponent,
    AutoRedirectRuleFormComponent,
    AutoTodoRuleFormComponent,
    FullCalendarComponent,
    UserEditComponent,
    UserNewComponent,
    UserFormComponent,
    WageBaseIndexComponent,
    WageBaseFormComponent,
    WageBaseShowComponent,
    LedgerAdjustmentFormComponent,
    LedgerNewComponent,
    SidekiqIndexComponent,
    ContactIndexComponent,
    ContactShowComponent,
    ContactSidebarComponent,
    ClipboardBtnComponent,
    AddressIndexComponent,
    AddressFormComponent,
    PhoneNumberIndexComponent,
    PhoneNumberFormComponent,
    AkaIndexComponent,
    AkaFormComponent,
    ContactTagsComponent,
    ContactRelationshipsComponent,
    ContactFormComponent,
    ContactNewsletterMailinglistsComponent,
    EmailAddressIndexComponent,
    EmailAddressFormComponent,
    ContactVendorsComponent,
    ContactVendorFormComponent,
    ContactNewComponent,
    ReminderCommonFormComponent,
    ReminderCommonComponent,
    DatetimePickerComponent,
    AutoReminderRuleFormComponent,
    AutoReminderRuleIndexComponent,
    PhonecallsIndexComponent,
    OutgoingJobsShowComponent,
    FaxIndexComponent,
    ScanPriorityIndexComponent,
    DocTemplateIndexComponent,
    DocTemplateFormComponent,
    StylesheetSearchComponent,
    HunspellSpellCheckIndexComponent,
    MatterUserIncomeComponent,
    MatterIndexComponent,
    IntranetShowComponent,
    IntranetFormComponent,
    ClientIntakeFieldFormComponent,
    ClientIntakeFieldTypeIndexComponent,
    ClientIntakeFieldTypeFormComponent,
    ClientIntakeIndexComponent,
    ClientIntakeFormComponent,
    ClientIntakeShowComponent,
    ConflictCheckComponent,
    NewsletterFormComponent,
    NewsletterShowComponent,
    NewsletterMailcampaignFormComponent,
    NewsletterFooterIndexComponent,
    NewsletterFooterFormComponent,
    NewsletterMailinglistIndexComponent,
    DocPartIndexComponent,
    TextDocumentIndexComponent,
    TextTemplateSearchComponent,
    SharesNewComponent,
    EmailNewComponent,
    StylesheetGroupsComponent,
    StylesheetsIndexComponent,
    StylesheetsFormComponent,
    FontIndexComponent,
    FontShowComponent,
    NewsletterFooterShowComponent,
    EmailEmbeddedImageSupportIndexComponent,
    ScanIndexComponent,
    ScanEditComponent,
    BillingIndexComponent,
    BillsIndexComponent,
    AccountingDashboardComponent,
    FactorIndexComponent,
    LedgerIndexComponent,
    CheckIndexComponent,
    DepositIndexComponent,
    ReceiptIndexComponent,
    ReceiptNewComponent,
    EntryGroupIndexComponent,
    ReceivableIndexComponent,
    WageIndexComponent,
    MultiSelectSearchComponent,
    FormTextInputComponent,
    FormCheckboxComponent,
    FormHelpTextComponent,
    FormSelectComponent,
    PaginationComponent,
    FormTextAreaComponent,
    FormDatePickerComponent,
    PageLoaderComponent,
    MatterInvoiceIndexComponent,
    BadgeComponent,
    PaginationCountComponent,
    OutgoingJobStatusComponent,
    OutgoingItemStatusComponent,
    FaxStatusComponent,
    OralDepositionStatusComponent,
    ButtonComponent,
    SearchBtnComponent,
    ClearSearchBtnComponent,
    SidebarToggleBtnComponent,
    FormGroupComponent,
    IconComponent,
    AppointmentRequestsSidebarComponent,
    NativeSortableJsDirective,
    PhoneCallBillingDirective,
    PhoneCallBillingModalComponent,
    FormLabelComponent,
    SpinnerComponent,
    LoadingTextComponent,
    LinkedAccountsComponent,
    SimpleModelFormComponent,
    GeoSearchComponent,
    FormControlComponent,
    FormFieldComponent,
    ActivityMatterRatesIndexComponent,
    ActivityMatterRatesShowComponent,
    DashboardReminderComponent,
    ManualIndexComponent,
    ManualSearchPipe
  ],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    FormsModule,
    ReactiveFormsModule,
    HttpClientModule,
    AppRoutingModule,
    PaginationModule.forRoot(),
    PopoverModule.forRoot(),
    ModalModule.forRoot(),
    TabsModule.forRoot(),
    TooltipModule.forRoot(),
    NgbNavModule,
    NgbModalModule,
    NgbDatepickerModule,
    NgbTimepickerModule,
    TypeaheadModule.forRoot(),
    BsDatepickerModule.forRoot(),
    SortableModule.forRoot(),
    FileUploadModule,
    ClipboardModule,
    CommonModule,
    NgbModule
    //NgSelectModule
  ],
  providers: [
    ActionCableService,

    DocumentChannelService,
    NotificationChannelService,

    AkaService,
    AuthService,
    AlertService,
    HttpClientService,
    AddressService,
    BillService,
    BillingService,
    BreadcrumbService,
    ClientIntakeService,
    ContactSearchService,
    CookieService,
    DataStoreService,
    DetailService,
    DocumentService,
    DocumentInboxService,
    DocumentTypeService,
    DocTemplateService,
    DocumentTemplateRoleNameService,
    EventService,
    FactorService,
    FaviconService,
    FaxService,
    IntranetSearchService,
    LinkedAccountService,
    LoggerService,
    LedgerService,
    MatterService,
    MatterDateService,
    MatterHistoryService,
    MatterSearchService,
    MatterStoreService,
    MatterUserService,
    ModalService,
    NotyService,
    OralDepositionService,
    OutgoingCheckService,
    OutgoingDocumentService,
    OutgoingEnvelopeService,
    OutgoingJobService,
    OutgoingLabelService,
    PhonecallService,
    PhoneCallBillingService,
    PhoneNumberService,
    QueryParamsService,
    SpamTitanService,

    AppointmentRequestService,
    AutoEmailRuleService,
    AutoOutgoingRuleService,
    AutoRedirectRuleService,
    AutoReminderRuleService,
    AutoTodoRuleService,
    GlobalTaxService,
    InvoiceService,
    InvolvementService,
    MatterTypeService,
    NewsletterMailinglistService,
    PaymentObligationService,
    ReminderService,
    RelationshipService,
    RruleService,
    SettingService,
    SettingGroupService,
    SettingTypeService,
    SidekiqService,
    StylesheetService,
    TagService,
    TaxService,
    TaxObligationService,
    TextDocumentService,
    TextDocumentTemplateService,
    ToBePaidService,
    TodoService,
    UserService,
    ValidationFunctionsService,
    VendorService,
    VendorSearchService,
    WageBaseService,
    WithheldIncomeTaxService,
    ContactService,
    ConflictService,
    SharesService,
    EmailService,
    IntranetGuard,
    ActivityMatterRatesService,
    CurrencyPipe,

    { provide: ErrorHandler, useClass: RollbarErrorHandler },
    { provide: RollbarService, useFactory: rollbarFactory },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
